import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";

/**
 * WebSocketContext
 *
 * Opens a single authenticated socket against /api/ws while there is a user.
 * Pages can subscribe to event types ("message" | "notification") via
 * `useWsEvent(event, handler)`. We also expose simple counters
 * (unreadNotifs, unreadMessages) that bump when events arrive and can be reset
 * from the page that just consumed them.
 */
const WsContext = createContext(null);

export function WebSocketProvider({ children }) {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const handlersRef = useRef(new Map()); // event -> Set<fn>
  const reconnectRef = useRef({ delay: 1000, timer: null });
  const [connected, setConnected] = useState(false);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const subscribe = (event, fn) => {
    const set = handlersRef.current.get(event) || new Set();
    set.add(fn);
    handlersRef.current.set(event, set);
    return () => {
      set.delete(fn);
    };
  };

  const connect = () => {
    if (!user || typeof user !== "object") return;
    const token = localStorage.getItem("elarte_token");
    if (!token) return;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl =
      base.replace(/^http/, "ws") + "/api/ws?token=" + encodeURIComponent(token);
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onopen = () => {
      setConnected(true);
      reconnectRef.current.delay = 1000;
    };
    ws.onclose = () => {
      setConnected(false);
      wsRef.current = null;
      // simple exponential backoff up to 20s
      if (user) {
        const d = Math.min(reconnectRef.current.delay * 1.8, 20000);
        reconnectRef.current.delay = d;
        reconnectRef.current.timer = setTimeout(connect, d);
      }
    };
    ws.onerror = () => {
      try {
        ws.close();
      } catch {}
    };
    ws.onmessage = (evt) => {
      let payload;
      try {
        payload = JSON.parse(evt.data);
      } catch {
        return;
      }
      const { event, data } = payload;
      if (event === "notification") setUnreadNotifs((n) => n + 1);
      if (event === "message") {
        // Only count as unread if the current user is the recipient.
        if (data?.recipient_id && data.recipient_id === user.id) {
          setUnreadMessages((n) => n + 1);
        }
      }
      const subs = handlersRef.current.get(event);
      if (subs) subs.forEach((fn) => fn(data));
    };
  };

  useEffect(() => {
    if (!user || typeof user !== "object") {
      setUnreadNotifs(0);
      setUnreadMessages(0);
      return;
    }
    connect();
    return () => {
      if (reconnectRef.current.timer) clearTimeout(reconnectRef.current.timer);
      try {
        wsRef.current?.close();
      } catch {}
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user && typeof user === "object" ? user.id : null]);

  // Prime unread counts from the REST endpoints once connected
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    let cancelled = false;
    const base = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
    const token = localStorage.getItem("elarte_token");
    const h = token ? { Authorization: `Bearer ${token}` } : {};
    Promise.all([
      fetch(`${base}/notifications/unread-count`, { credentials: "include", headers: h }).then((r) => r.json()).catch(() => ({ unread: 0 })),
      fetch(`${base}/conversations/unread-count`, { credentials: "include", headers: h }).then((r) => r.json()).catch(() => ({ unread: 0 })),
    ]).then(([n, m]) => {
      if (cancelled) return;
      setUnreadNotifs(n.unread || 0);
      setUnreadMessages(m.unread || 0);
    });
    return () => {
      cancelled = true;
    };
  }, [user && typeof user === "object" ? user.id : null]);

  const value = {
    connected,
    unreadNotifs,
    unreadMessages,
    setUnreadNotifs,
    setUnreadMessages,
    subscribe,
  };

  return <WsContext.Provider value={value}>{children}</WsContext.Provider>;
}

export const useWs = () => useContext(WsContext) || {};

/** Subscribe to a named event for the lifetime of the component. */
export function useWsEvent(event, handler) {
  const { subscribe } = useWs();
  useEffect(() => {
    if (!subscribe) return;
    return subscribe(event, handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, handler]);
}
